import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, AlarmClock, IndianRupee, CheckCircle2 } from "lucide-react";
import { BRAND, inr } from "./constants";

function ReminderRow({ x, onSend }) {
  return (
    <div data-testid={`reminder-${x.kind}-${x.booking_id}`} className="py-3 border-t text-sm" style={{ borderColor: BRAND.line }}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <b>{x.guest_name}</b> <span className="text-xs" style={{ color: BRAND.muted }}>{x.bed_label ? `Bed ${x.bed_label} · ` : ""}{x.guest_phone || "no phone"}{x.kind === "rent" ? ` · ${inr(x.amount_inr)} due ${x.due_date}` : ""}</span>
        </div>
        {x.sent
          ? <span data-testid={`reminder-sent-${x.kind}-${x.booking_id}`} className="text-[11px] font-bold inline-flex items-center gap-1" style={{ color: BRAND.green }}><CheckCircle2 className="w-3.5 h-3.5" /> Sent today</span>
          : <a href={x.wa_url} target="_blank" rel="noreferrer" onClick={() => onSend(x)} data-testid={`reminder-send-${x.kind}-${x.booking_id}`}
              className="rounded-full px-3 py-1.5 text-xs font-bold text-white inline-flex items-center gap-1 shrink-0" style={{ background: "#16A34A" }}>
              <MessageCircle className="w-3.5 h-3.5" /> Send on WhatsApp
            </a>}
      </div>
      <pre className="mt-1.5 text-[11px] whitespace-pre-wrap font-sans rounded-lg p-2" style={{ background: "#F6F8F7", color: "#344054" }}>{x.text}</pre>
    </div>
  );
}

export function RemindersPanel({ pid, authFetch, API }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/${pid}/reminders` }).then((r) => setD(r.data)).catch(() => {}), [pid, authFetch, API]);
  useEffect(() => { load(); }, [load]);

  const send = (x) => {
    authFetch({ method: "post", url: `${API}/homestay/properties/${pid}/reminders/log`, data: { booking_id: x.booking_id, kind: x.kind } })
      .then(() => { toast.success("Marked as sent"); load(); }).catch(() => {});
  };

  if (!d) return <div className="text-sm" style={{ color: BRAND.muted }}>Loading reminders…</div>;
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="reminders-view">
      <div data-testid="reminders-checkout" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
        <div className="text-sm font-bold inline-flex items-center gap-1.5" style={{ color: BRAND.green }}><AlarmClock className="w-4 h-4" style={{ color: BRAND.orange }} /> Per-day checkouts today · {d.checkout_hour}:00 AM</div>
        <p className="text-xs mt-0.5" style={{ color: BRAND.muted }}>Send at 8 AM (1 hour before checkout). Late checkout: ₹{d.late_fee_per_hour}/extra hour — add it under Bookings & Payments → Income → "Late checkout fee".</p>
        {d.checkout.length === 0 && <div className="text-xs mt-3" style={{ color: BRAND.muted }}>No per-day checkouts today.</div>}
        {d.checkout.map((x) => <ReminderRow key={x.booking_id} x={x} onSend={send} />)}
      </div>
      <div data-testid="reminders-rent" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
        <div className="text-sm font-bold inline-flex items-center gap-1.5" style={{ color: BRAND.green }}><IndianRupee className="w-4 h-4" style={{ color: BRAND.orange }} /> Monthly rent due in 3 days</div>
        <p className="text-xs mt-0.5" style={{ color: BRAND.muted }}>Due on the same date each month as check-in. Shows from 3 days before until the due date.</p>
        {d.rent.length === 0 && <div className="text-xs mt-3" style={{ color: BRAND.muted }}>No rent due in the next 3 days.</div>}
        {d.rent.map((x) => <ReminderRow key={x.booking_id} x={x} onSend={send} />)}
      </div>
    </div>
  );
}
